import type {IModifyTemplateGraph, NumberLike} from "@cm/template-nodes"
import type {
    InitStackableMattressStandardModelMasterTemplateOptions,
    InitMattressInstanceTemplateOptions,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {initInstanceTemplateCommon} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {getComponentGraphNodesForBottomPanel} from "#mattress/assets-definitions/standard-models/common/parts"
import {getComponentGraphNodesForNonStackableStandardModelDimensionsLogic} from "#mattress/assets-definitions/standard-models/common/dimension-logic"
import {
    getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate,
    getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate,
} from "#mattress/assets-definitions/standard-models/common/material-offset"
import type {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import {createMattressGroup, createMattress, createMattressMesh, createMattressCut} from "#mattress/assets-definitions/create-mattress"
import {createTemplateReference, createMaterialInput, createMaterialReference} from "#mattress/assets-definitions/common"
import {uiPathMetaData, createPath, panelPathName} from "#mattress/mattress-meta-data"
import {MattressCurve} from "#mattress/nodes/curve/mattress-curve"
import {headEndPanelControlPoints, footEndPanelControlPoints} from "#mattress/assets-definitions/standard-models/common/uv-constants"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"

function getComponentGraphNodesForWrapPart(inputData: {
    bottomPanelCurve: MattressCurveLikeNode
    defaultMaterialLegacyId?: number
    fabricLayout?: MattressFabricLayout
    width: NumberLike
    length: NumberLike
    height: NumberLike
    bottomRadius: NumberLike
    topAndBottomPartMaterialOffsetComponentGraph: MattressComponentGraph
}) {
    const {
        bottomPanelCurve,
        defaultMaterialLegacyId,
        fabricLayout = "AllOverWrap",
        width,
        length,
        height,
        bottomRadius,
        topAndBottomPartMaterialOffsetComponentGraph,
    } = inputData

    const material = createMaterialInput(
        "Wrap Material",
        "wrap-material",
        uiPathMetaData([createPath(panelPathName, 0)], 0),
        defaultMaterialLegacyId ? createMaterialReference(defaultMaterialLegacyId) : undefined,
    )

    const {materialOffsetComponentInstance: wrapMaterialOffsetInstance, materialOffsetOutput: wrapMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Wrap Material Offset",
            componentInstanceId: "wrap-material-offset",
            outputName: "Wrap Material Offset",
            material,
            width,
            length,
            heigth: height,
            edgeRadius: bottomRadius,
        })
    const cut = createMattressCut("Wrap", [{curve: bottomPanelCurve, side: "right"}], fabricLayout, wrapMaterialOffset)

    const nodes: MattressComponentGraphNode[] = [material, wrapMaterialOffsetInstance, wrapMaterialOffset, cut]
    return {nodes}
}

const END_PANEL_CUT_SIDE = "right" as const

function getComponentGraphNodesForEndPanel(inputData: {
    end: "head" | "foot"
    defaultMaterialLegacyId?: number
    fabricLayout?: MattressFabricLayout
    uiRank: number
}) {
    const {end, defaultMaterialLegacyId, fabricLayout = "BorderWithMirror", uiRank} = inputData
    const label = end === "head" ? "Head Panel" : "Foot Panel"

    const curve = new MattressCurve({
        parameters: {
            name: `${label} Curve`,
            closed: true,
            resolution: 0.5,
            controlPoints: end === "head" ? headEndPanelControlPoints : footEndPanelControlPoints,
        },
    })
    const material = createMaterialInput(
        `${label} Material`,
        `${end}-panel-material`,
        uiPathMetaData([createPath(panelPathName, uiRank)], 0),
        defaultMaterialLegacyId ? createMaterialReference(defaultMaterialLegacyId) : undefined,
    )
    const cut = createMattressCut(label, [{curve, side: END_PANEL_CUT_SIDE}], fabricLayout, material)

    const nodes: MattressComponentGraphNode[] = [curve, material, cut]
    return {nodes, curve}
}

function initMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions,
    bottomPanelMode: "mirror" | "edge",
) {
    const dimensions = getComponentGraphNodesForNonStackableStandardModelDimensionsLogic()

    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {nodes: bottomPanelNodes, curve: bottomPanelCurve} = getComponentGraphNodesForBottomPanel({
        defaultBottomPanelMaterialLegacyId: options.bottomPartDefaultMaterialLegacyId,
        mode: bottomPanelMode,
        fabricLayout: options.bottomPartFabricLayout ?? "Waterfall",
        topAndBottomPartMaterialOffsetComponentGraph,
        width: dimensions.width,
        length: dimensions.length,
    })
    const bottomPanelGroup = createMattressGroup("Bottom Panel", [topAndBottomPartMaterialOffsetComponentGraph, ...bottomPanelNodes])

    const {nodes: wrapNodes} = getComponentGraphNodesForWrapPart({
        bottomPanelCurve,
        defaultMaterialLegacyId: options.topPartDefaultMaterialLegacyId,
        fabricLayout: options.topPartFabricLayout ?? "AllOverWrap",
        topAndBottomPartMaterialOffsetComponentGraph,
        width: dimensions.width,
        length: dimensions.length,
        height: dimensions.height,
        bottomRadius: dimensions.safeBottomRadius,
    })
    const wrapGroup = createMattressGroup("Wrap", wrapNodes)

    const endPanelFabricLayout = options.middlePartFabricLayout ?? "BorderWithMirror"
    const {nodes: headPanelNodes} = getComponentGraphNodesForEndPanel({
        end: "head",
        defaultMaterialLegacyId: options.middlePartDefaultMaterialLegacyId,
        fabricLayout: endPanelFabricLayout,
        uiRank: 1,
    })
    const headPanelGroup = createMattressGroup("Head Panel", headPanelNodes)
    const {nodes: footPanelNodes} = getComponentGraphNodesForEndPanel({
        end: "foot",
        defaultMaterialLegacyId: options.middlePartDefaultMaterialLegacyId,
        fabricLayout: endPanelFabricLayout,
        uiRank: 2,
    })
    const footPanelGroup = createMattressGroup("Foot Panel", footPanelNodes)

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width: dimensions.width,
        length: dimensions.length,
        height: dimensions.height,
        cornerRadius: dimensions.cornerRadius,
        topRadius: dimensions.safeTopRadius,
        bottomRadius: dimensions.safeBottomRadius,
    })
    const dimensionsGroup = createMattressGroup("Dimensions", [
        dimensions.width,
        dimensions.length,
        dimensions.height,
        dimensions.cornerRadius,
        dimensions.topRadius,
        dimensions.bottomRadius,
        dimensions.safeIntermediateBottomRadius,
        dimensions.safeIntermediateTopRadius,
        dimensions.safeBottomRadius,
        dimensions.safeTopRadius,
    ])

    // Group order is cut order
    const mattress = createMattress([mattressMesh, dimensionsGroup, wrapGroup, headPanelGroup, footPanelGroup, bottomPanelGroup])

    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

export function initAllOverWrapMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    return initMasterTemplate(modifyTemplateGraph, options, "edge")
}

export function initAllOverWrapWithMirrorMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    return initMasterTemplate(modifyTemplateGraph, options, "mirror")
}

function initInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
    instance: {instanceName: string; instanceId: string},
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 92,
        length: 200,
        height: 25,
        "corner-radius": 2,
        "top-radius": 2,
        "bottom-radius": 2,
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            ...instance,
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initAllOverWrapInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    initInstanceTemplate(modifyTemplateGraph, masterTemplateDataLegacyId, options, {
        instanceName: "All Over Wrap (Master)",
        instanceId: "all-over-wrap-master",
    })
}

export function initAllOverWrapWithMirrorInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    initInstanceTemplate(modifyTemplateGraph, masterTemplateDataLegacyId, options, {
        instanceName: "All Over Wrap with Mirror (Master)",
        instanceId: "all-over-wrap-with-mirror-master",
    })
}
